import React, { Suspense, useCallback, useState } from "react";

import {
  PageBuilderCardMosaicFragment,
  PageBuilderCardMosaicModalItemFragment
} from "types/graphql-types";
import { MosaicLayoutVariantType } from "@silversea-ssc/ui-components-react";
import PageBuilderSectionTitle from "../PageBuilderSectionTitle";
import { graphql } from "gatsby";

import { BackgroundColor } from "types/componentBackground";
import { cardColorForBackground } from "../pageBuilderRenderHelper";

import {
  getAllAnchorableSections,
  getAnchorableSectionByRefName,
  scrollToSection
} from "src/utils/inPageNavigation";
import { CardVariant } from "./PageBuilderCardMosaicStandard";

const PageBuilderCardMosaicStandard = React.lazy(
  () => import("./PageBuilderCardMosaicStandard")
);
const PageBuilderCardMosaicLinks = React.lazy(
  () => import("./PageBuilderCardMosaicLinks")
);
const PageBuilderCardMosaicModal = React.lazy(
  () => import("./PageBuilderCardMosaicModal")
);

export type PageBuilderCardMosaicProps = PageBuilderCardMosaicFragment & {
  backgroundColor: BackgroundColor;
};

type PageBuilderCardMosaicVariant = MosaicLayoutVariantType | "link-cards";

const PageBuilderCardMosaic: React.FC<
  React.PropsWithChildren<PageBuilderCardMosaicProps>
> = ({ heading, items = [], mosaicVariant, cardVariant, backgroundColor }) => {
  const [showModal, setShowModal] = useState<
    PageBuilderCardMosaicModalItemFragment | undefined
  >(undefined);

  const cardBackgroundColor = cardColorForBackground(backgroundColor);
  const mosaicColumnVariant = (mosaicVariant ||
    "column3") as PageBuilderCardMosaicVariant;
  const mosaicCardVariant = (cardVariant || "default") as CardVariant;

  const onClickAnchor = useCallback((target: string | undefined) => {
    const navContainerItems = getAllAnchorableSections();
    const ref = getAnchorableSectionByRefName(navContainerItems, target);

    if (ref) scrollToSection(ref);
  }, []);

  return (
    <React.Fragment>
      <PageBuilderSectionTitle {...heading} />
      <Suspense fallback={null}>
        {mosaicColumnVariant === "link-cards" ? (
          <PageBuilderCardMosaicLinks
            items={items}
            onShowModal={setShowModal}
            onClickAnchor={onClickAnchor}
          />
        ) : (
          <PageBuilderCardMosaicStandard
            items={items}
            cardBackgroundColor={cardBackgroundColor}
            mosaicCardVariant={mosaicCardVariant}
            mosaicColumnVariant={mosaicColumnVariant}
            onShowModal={setShowModal}
            onClickAnchor={onClickAnchor}
          />
        )}
      </Suspense>
      {showModal && (
        <Suspense>
          <PageBuilderCardMosaicModal
            {...showModal}
            modal={{
              isOpen: true,
              onRequestClose: () => setShowModal(undefined)
            }}
          />
        </Suspense>
      )}
    </React.Fragment>
  );
};

export default PageBuilderCardMosaic;

export const fragments = graphql`
  fragment PageBuilderCardMosaicItem on SanityMosaicCard {
    _key
    image {
      ...ImageWithAlt
    }

    title {
      localized
    }

    tagline {
      localized
    }

    description {
      localized
    }

    ctaButtons {
      ...ButtonCTA
    }
  }
  fragment PageBuilderCardMosaicModalItem on SanityMosaicCardModal {
    _key
    image {
      ...ImageWithAlt
    }

    title {
      en
      localized
    }

    tagline {
      localized
    }

    description {
      localized
    }

    modalTitle {
      localized
    }

    modalContent {
      localized
    }
  }

  fragment PageBuilderCardMosaic on SanityCardMosaic {
    _key
    sectionLayoutConfig {
      ...SectionLayoutConfig
    }
    heading {
      ...SectionTitle
      ...SectionTitleCTAs
    }
    items {
      ... on SanityMosaicCard {
        __typename
        ...PageBuilderCardMosaicItem
      }

      ... on SanityMosaicCardModal {
        __typename
        ...PageBuilderCardMosaicModalItem
      }
    }
    mosaicVariant
    cardVariant
  }
`;
